'use client'

import styled from '@emotion/styled'
import WalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import BusinessIcon from '@mui/icons-material/Business'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GavelIcon from '@mui/icons-material/Gavel'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PublicIcon from '@mui/icons-material/Public'
import ScienceIcon from '@mui/icons-material/Science'
import VerifiedIcon from '@mui/icons-material/Verified'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import { useState } from 'react'

const HeroSection = styled.section`
  padding-top: calc(var(--header-height) + 48px);
  padding-bottom: 96px;
  background: linear-gradient(135deg, #0a1929 0%, #0f2942 50%, #0a1929 100%);
`

const Badge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(39, 177, 198, 0.1);
  border: 1px solid rgba(39, 177, 198, 0.2);
  border-radius: 9999px;
  padding: 6px 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0e7490;
  margin-bottom: 24px;
`

const LightCard = styled(Card)`
  height: 100%;
  background: #fafafa !important;
  background-image: none !important;
  border: none;
  border-radius: 12px;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 4px 6px -1px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    box-shadow:
      0 1px 2px -1px rgba(0, 0, 0, 0.1),
      0 8px 10px -1px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
  }
`

const MSB_STATES = [
  'Alabama',
  'Alaska',
  'American Samoa',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District Of Columbia',
  'Federated States Of Micronesia',
  'Florida',
  'Georgia',
  'Guam',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Marshall Islands',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Northern Mariana Islands',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Palau',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Virgin Islands, US',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
]

const EU_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
]

const EEA_COUNTRIES = ['Iceland', 'Liechtenstein', 'Norway']

const CANADA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
]

const faqs = [
  {
    q: 'What services do you offer?',
    a: 'We provide AI-powered automated trading execution technology for cryptocurrency markets. Our platform offers access to predefined, non-discretionary spot trading strategies powered by our proprietary Polar One AI system.',
  },
  {
    q: 'Who can use the platform?',
    a: 'We cater to a diverse range of clients, including private individuals, family offices, corporates, and institutional investors seeking to gain exposure to or enhance their investments in the digital asset space.',
  },
  {
    q: 'How do you ensure the security of assets?',
    a: "Security is paramount. We employ a multi-layered approach that includes cutting-edge cybersecurity measures, cold storage solutions for digital assets, and continuous AI-driven risk monitoring to protect our clients' investments.",
  },
  {
    q: 'Is there a minimum deposit amount?',
    a: 'Minimum investment amounts can vary depending on the specific fund or strategy. We encourage you to contact us for a consultation to discuss your financial goals and the investment options best suited for you.',
  },
]

const Dot = ({ color = '#0e7490' }: { color?: string }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
      mt: 0.75,
    }}
  />
)

export default function AboutUsPage() {
  const [statesOpen, setStatesOpen] = useState(false)
  const [countriesOpen, setCountriesOpen] = useState(false)
  const [provincesOpen, setProvincesOpen] = useState(false)

  return (
    <>
      {/* Hero */}
      <HeroSection>
        <Container maxWidth='lg'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto' }}
          >
            <Badge sx={{ color: '#67e8f9' }}>Our Vision</Badge>
            <Typography
              variant='h1'
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.5rem', lg: '3rem' },
              }}
            >
              Bridging AI Research with Wealth Generation
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              We explore uncharted territories in artificial intelligence and
              mathematical modeling, developing novel approaches that haven&apos;t
              been tested in traditional finance.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Our mission is to transform cutting-edge research into tangible
              financial outcomes, proving that the most advanced mathematical
              concepts can create real value.
            </Typography>
          </motion.div>
        </Container>
      </HeroSection>

      {/* Company Overview */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth='lg'>
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
              <BusinessIcon sx={{ fontSize: 32, color: '#0e7490' }} />
              <Typography variant='h3' sx={{ fontWeight: 700, color: '#171717' }}>
                Company Overview
              </Typography>
            </Box>
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '1.0625rem',
                lineHeight: 1.7,
                mb: 8,
              }}
            >
              Polar Tensor™ is a quantitative finance and research company focused on
              innovative solutions in the financial sector. We maintain active
              registrations and regulatory compliance across multiple jurisdictions
              including Panama, Hong Kong, the United States, and the European Union,
              with all necessary documentation and filings accessible for
              transparency.
            </Typography>

            {/* User Onboarding Entities */}
            <Typography
              variant='h5'
              sx={{ fontWeight: 700, color: '#0e7490', mb: 3 }}
            >
              User Onboarding Entities
            </Typography>
            <Grid container spacing={3} sx={{ mb: 8 }}>
              {/* USA Wyoming */}
              <Grid size={{ xs: 12, md: 4 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇺🇸</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        USA
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Polar Tensor™ US
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Jurisdiction', 'State of Wyoming'],
                        ['Registration Number', '2026-001864053'],
                        ['MSB Registration Number', '31000321683570'],
                        ['EIN', '98-1912874'],
                        ['MTL License', 'Submitted'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {label}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                label !== 'Jurisdiction' && label !== 'MTL License'
                                  ? 'monospace'
                                  : undefined,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/us-msb-license.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        mb: 1,
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      License
                    </MuiButton>
                    <MuiButton
                      variant='outlined'
                      fullWidth
                      startIcon={<LocationOnIcon />}
                      onClick={() => setStatesOpen(true)}
                      sx={{
                        textTransform: 'none',
                        color: '#171717',
                        borderColor: '#d1d5db',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                        },
                      }}
                    >
                      View States of Activity
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>

              {/* Canada Ontario */}
              <Grid size={{ xs: 12, md: 4 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇨🇦</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Canada
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Bitbanki Ltd.
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Jurisdiction', 'Province of Ontario'],
                        ['Registration Number', '1001339292'],
                        ['MSB Registration Number', 'C10001290'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {label}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                label !== 'Jurisdiction' ? 'monospace' : undefined,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<PublicIcon />}
                      component={Link}
                      href='https://fintrac-canafe.canada.ca/msb-esm/reg-eng'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        mb: 1,
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Fintrac Register
                    </MuiButton>
                    <MuiButton
                      variant='outlined'
                      fullWidth
                      startIcon={<LocationOnIcon />}
                      onClick={() => setProvincesOpen(true)}
                      sx={{
                        textTransform: 'none',
                        color: '#171717',
                        borderColor: '#d1d5db',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                        },
                      }}
                    >
                      View Provinces of Activity
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>

              {/* Europe Poland */}
              <Grid size={{ xs: 12, md: 4 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇪🇺</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Europe
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717' }}>
                      FIERES LIMITED SP ZOO
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{ color: '#9ca3af', mb: 2, display: 'block' }}
                    >
                      Trading as Polar Tensor™ Europe SP ZOO
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Jurisdiction', 'Republic of Poland'],
                        ['NIP (Tax ID)', '5214007195'],
                        ['KRS (Registry Number)', '0001021221'],
                        ['RDWW (Virtual Currency Registry)', 'RDWW-673'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {label}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                label !== 'Jurisdiction' ? 'monospace' : undefined,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/eu-crypto-license.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        mb: 1,
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      License
                    </MuiButton>
                    <MuiButton
                      variant='outlined'
                      fullWidth
                      startIcon={<LocationOnIcon />}
                      onClick={() => setCountriesOpen(true)}
                      sx={{
                        textTransform: 'none',
                        color: '#171717',
                        borderColor: '#d1d5db',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                        },
                      }}
                    >
                      View Countries of Activity
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>

              {/* Africa Nigeria */}
              <Grid size={{ xs: 12, md: 4 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇳🇬</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Africa
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Polar Tensor™ Africa Limited
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Jurisdiction', 'Federal Republic of Nigeria'],
                        ['Company Registration No.', '9335583'],
                        ['Tax Identification Number', '2622461173246'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {label}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                label !== 'Jurisdiction' ? 'monospace' : undefined,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/africa-certificate.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        mb: 1,
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Certificate
                    </MuiButton>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/scuml.pdf'
                      target='_blank'
                      download
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      SCUML Registration
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>

            {/* Providers */}
            <Typography
              variant='h5'
              sx={{ fontWeight: 700, color: '#0e7490', mb: 3 }}
            >
              Providers
            </Typography>

            {/* Tech */}
            <Typography sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
              Tech
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇵🇦</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Panama
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Polar Tensor™ Corp.
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Role', 'AI Trading Execution'],
                        ['Jurisdiction', 'Republic of Panama'],
                        ['Registration Number', '155771852'],
                        ['SEC (USA) CIK Number', '0002085242'],
                      ].map(([l, v]) => (
                        <Box key={l} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {l}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                l !== 'Role' && l !== 'Jurisdiction'
                                  ? 'monospace'
                                  : undefined,
                            }}
                          >
                            {v}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/panama-certificate.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Certificate
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇭🇰</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Hong Kong
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Polar Tensor™ LAB Limited
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Role', 'Tech & Research'],
                        ['Jurisdiction', 'Hong Kong SAR'],
                        ['Registration Number', '78737300'],
                      ].map(([l, v]) => (
                        <Box key={l} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {l}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                l !== 'Role' && l !== 'Jurisdiction'
                                  ? 'monospace'
                                  : undefined,
                            }}
                          >
                            {v}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/hongkong-certificate.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Certificate
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>

            {/* Payment Processing */}
            <Typography sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
              Payment Processing
            </Typography>
            <Grid container spacing={3} sx={{ mb: 8 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇺🇸</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        USA
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 2 }}>
                      Polar MSB Inc.
                    </Typography>
                    <Box sx={{ flex: 1, mb: 2 }}>
                      {[
                        ['Role', 'Payment Processor'],
                        ['Jurisdiction', 'State of Montana'],
                        ['Registration Number', 'D1537006'],
                        ['FinCEN Registration Number', '31000306664168'],
                      ].map(([l, v]) => (
                        <Box key={l} sx={{ mb: 1 }}>
                          <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                            {l}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: '#171717',
                              fontFamily:
                                l !== 'Role' && l !== 'Jurisdiction'
                                  ? 'monospace'
                                  : undefined,
                            }}
                          >
                            {v}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/montana-msb-license.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      License
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>

            {/* Licenses in Progress */}
            <Typography
              variant='h5'
              sx={{ fontWeight: 700, color: '#0e7490', mb: 1 }}
            >
              Licenses in Progress
            </Typography>
            <Chip
              label='PENDING'
              size='small'
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor: 'rgba(234,179,8,0.1)',
                color: '#ca8a04',
                border: '1px solid rgba(234,179,8,0.3)',
              }}
            />
            <Grid container spacing={3}>
              {[{ flag: '🇸🇻', country: 'El Salvador', license: 'VASP License' }].map(
                (item, i) => (
                  <Grid key={i} size={{ xs: 12, md: 4 }}>
                    <LightCard elevation={0}>
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: '1.5rem' }}>
                            {item.flag}
                          </Typography>
                          <Typography
                            variant='h6'
                            sx={{ fontWeight: 700, color: '#171717' }}
                          >
                            {item.country}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: '#171717' }}>
                          {item.license}
                        </Typography>
                      </CardContent>
                    </LightCard>
                  </Grid>
                ),
              )}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Legal Documents & Compliance */}
      <Box sx={{ py: 12, backgroundColor: '#ffffff' }}>
        <Container maxWidth='lg'>
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            {/* KPMG Audited */}
            <Card
              sx={{
                backgroundColor: '#fafafa !important',
                backgroundImage: 'none !important',
                border: '1px solid rgba(14,116,144,0.2)',
                borderRadius: 3,
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
                mb: 6,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <VerifiedIcon sx={{ fontSize: 28, color: '#0e7490' }} />
                  <Typography
                    variant='h5'
                    sx={{ fontWeight: 700, color: '#171717' }}
                  >
                    KPMG Audited
                  </Typography>
                  <Box
                    component='img'
                    src='/kpmg-logo.svg'
                    alt='KPMG'
                    sx={{
                      height: 36,
                      width: 'auto',
                      opacity: 0.8,
                      ml: 'auto',
                    }}
                  />
                </Box>
                <Typography sx={{ color: '#6b7280', lineHeight: 1.7, mb: 3 }}>
                  Our entire legal setup has been reviewed and verified by KPMG, one
                  of the Big 4 global auditing firms.
                </Typography>
                <MuiButton
                  variant='contained'
                  fullWidth
                  startIcon={<DownloadIcon />}
                  component={Link}
                  href='/legal/kpmg.pdf'
                  target='_blank'
                  sx={{
                    backgroundColor: '#0e7490',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#155e75', color: 'white' },
                    color: 'white',
                  }}
                >
                  KPMG Audit Report
                </MuiButton>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 6 }}>
              <GavelIcon sx={{ fontSize: 32, color: '#0e7490' }} />
              <Typography variant='h3' sx={{ fontWeight: 700, color: '#171717' }}>
                Legal Documents & Compliance
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                    >
                      SEC Filing{' '}
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{ color: '#9ca3af', fontWeight: 400 }}
                      >
                        (For Institutions Only)
                      </Typography>
                    </Typography>
                    <Typography sx={{ color: '#6b7280', mb: 3, flex: 1 }}>
                      Our SEC filing under Rule 506(b) of Regulation D, providing
                      exemption for private placements.
                    </Typography>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='https://www.sec.gov/Archives/edgar/data/2085242/000208524225000001/xslFormDX01/primary_doc.xml'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      SEC Filing
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                    >
                      Legal Opinion{' '}
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{ color: '#9ca3af', fontWeight: 400 }}
                      >
                        (For Institutions Only)
                      </Typography>
                    </Typography>
                    <Typography sx={{ color: '#6b7280', mb: 3, flex: 1 }}>
                      Comprehensive legal opinion document outlining our regulatory
                      compliance and legal standing.
                    </Typography>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/legal-opinion.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Legal Opinion
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>

            {/* Retail Legal Opinions */}
            <Typography
              variant='h5'
              sx={{ fontWeight: 700, color: '#0e7490', mb: 3 }}
            >
              Retail Legal Opinions
            </Typography>
            <Grid container spacing={3} sx={{ mb: 8 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇺🇸</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        United States
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ color: '#6b7280', lineHeight: 1.7, flex: 1, mb: 2 }}
                    >
                      Legal opinion confirming compliance with US federal and state
                      regulations for retail users.
                    </Typography>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/us-legal-opinion.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Legal Opinion
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>🇪🇺</Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        Europe
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ color: '#6b7280', lineHeight: 1.7, flex: 1, mb: 2 }}
                    >
                      Legal opinion confirming compliance with EU regulatory
                      frameworks and MiCA readiness.
                    </Typography>
                    <MuiButton
                      variant='contained'
                      fullWidth
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href='/legal/eu-lo.pdf'
                      target='_blank'
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Legal Opinion
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>

            {/* Regulatory Compliance */}
            <Card
              sx={{
                backgroundColor: '#fafafa !important',
                backgroundImage: 'none !important',
                border: '1px solid rgba(14,116,144,0.2)',
                borderRadius: 3,
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant='h5'
                  sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                >
                  Regulatory Compliance
                </Typography>
                <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                  All registrations and filings are current and maintained in good
                  standing. We are committed to full transparency and regulatory
                  compliance across all jurisdictions in which we operate. For any
                  questions regarding our legal status or to request additional
                  documentation, please contact our legal team.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Polar Ecosystem */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth='lg'>
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Badge>
                <PublicIcon sx={{ fontSize: 16 }} />
                Polar Ecosystem
              </Badge>
              <Typography
                variant='h3'
                sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
              >
                The Polar Corporation Family
              </Typography>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: '1.0625rem',
                  maxWidth: 700,
                  mx: 'auto',
                  mb: 4,
                }}
              >
                Polar Corporation is the project company overseeing a unified
                ecosystem of specialized entities, each dedicated to a distinct
                pillar of the financial technology landscape.
              </Typography>
              <MuiButton
                variant='outlined'
                endIcon={<ArrowForwardIcon />}
                component='a'
                href='https://polar-corporation.com'
                target='_blank'
                sx={{
                  textTransform: 'none',
                  color: '#171717',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                Visit Polar Corporation
              </MuiButton>
            </Box>

            <Grid container spacing={3}>
              {/* Polar Money */}
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          background:
                            'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.1))',
                          border: '1px solid rgba(16,185,129,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <WalletIcon sx={{ fontSize: 24, color: '#10b981' }} />
                      </Box>
                      <Chip
                        label='Financial Services'
                        size='small'
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.6875rem',
                          backgroundColor: 'rgba(16,185,129,0.1)',
                          color: '#059669',
                          border: '1px solid rgba(16,185,129,0.2)',
                        }}
                      />
                    </Box>
                    <Typography
                      variant='h5'
                      sx={{ fontWeight: 700, mb: 1.5, color: '#171717' }}
                    >
                      Polar Money™
                    </Typography>
                    <Typography sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
                      Global payment infrastructure powering next-generation
                      financial services. IBAN accounts, VISA® Platinum cards, crypto
                      wallets, and seamless currency exchange.
                    </Typography>
                    <Box sx={{ flex: 1, mb: 3 }}>
                      {[
                        'EUR & USD IBAN Accounts',
                        'VISA® Platinum Cards',
                        'Crypto Wallets',
                        'Fiat-to-Crypto Exchange',
                      ].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <Dot />
                          <Typography sx={{ color: '#6b7280' }}>{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      component='a'
                      href='https://www.polar-money.com'
                      target='_blank'
                      sx={{
                        background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(90deg, #059669, #0891b2)',
                          color: 'white',
                        },
                      }}
                    >
                      Visit Website
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>

              {/* Polar Research */}
              <Grid size={{ xs: 12, md: 6 }}>
                <LightCard elevation={0}>
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          background:
                            'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.1))',
                          border: '1px solid rgba(168,85,247,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ScienceIcon sx={{ fontSize: 24, color: '#a855f7' }} />
                      </Box>
                      <Chip
                        label='Scientific Research'
                        size='small'
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.6875rem',
                          backgroundColor: 'rgba(168,85,247,0.1)',
                          color: '#9333ea',
                          border: '1px solid rgba(168,85,247,0.2)',
                        }}
                      />
                    </Box>
                    <Typography
                      variant='h5'
                      sx={{ fontWeight: 700, mb: 1.5, color: '#171717' }}
                    >
                      Polar Research™
                    </Typography>
                    <Typography sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
                      Scientific research division developing cutting-edge trading
                      algorithms and conducting pioneering field research in
                      quantitative finance.
                    </Typography>
                    <Box sx={{ flex: 1, mb: 3 }}>
                      {[
                        'Quantitative Research',
                        'Algorithm Development',
                        'Market Analysis',
                        'Academic Partnerships',
                      ].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <Dot />
                          <Typography sx={{ color: '#6b7280' }}>{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <MuiButton
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      component='a'
                      href='https://polar-research.com'
                      target='_blank'
                      sx={{
                        background: 'linear-gradient(90deg, #a855f7, #ec4899)',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(90deg, #9333ea, #db2777)',
                          color: 'white',
                        },
                      }}
                    >
                      Visit Website
                    </MuiButton>
                  </CardContent>
                </LightCard>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: 12, backgroundColor: '#ffffff' }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Badge>FAQ</Badge>
            <Typography
              variant='h3'
              sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '1.125rem',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Everything you need to know about our AI trading platform
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Card
              sx={{
                backgroundColor: '#fafafa !important',
                backgroundImage: 'none !important',
                border: 'none',
                borderRadius: 3,
                boxShadow: '0 8px 10px -1px rgba(0,0,0,0.12)',
              }}
            >
              {faqs.map((faq, i) => (
                <Accordion
                  key={i}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent !important',
                    backgroundImage: 'none !important',
                    '&:before': { display: 'none' },
                    borderBottom: i < faqs.length - 1 ? '1px solid #e5e7eb' : 'none',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#9ca3af' }} />}
                    sx={{ px: 4, py: 1 }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#171717' }}>
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 4, pb: 3 }}>
                    <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Card>
          </Box>
        </Container>
      </Box>

      {/* States Dialog */}
      <Dialog
        open={statesOpen}
        onClose={() => setStatesOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff !important',
            backgroundImage: 'none !important',
          },
        }}
      >
        <DialogTitle sx={{ color: '#171717' }}>States of MSB Activities</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ color: '#6b7280', mb: 3 }}>
            Polar Tensor US is registered for MSB activities across the listed US
            states and territories.
          </Typography>
          <Grid container spacing={1}>
            {MSB_STATES.map((s) => (
              <Grid key={s} size={{ xs: 6, sm: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Dot />
                  <Typography variant='body2' sx={{ color: '#171717' }}>
                    {s}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Countries Dialog */}
      <Dialog
        open={countriesOpen}
        onClose={() => setCountriesOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff !important',
            backgroundImage: 'none !important',
          },
        }}
      >
        <DialogTitle sx={{ color: '#171717' }}>Countries of Activity</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ color: '#6b7280', mb: 3 }}>
            FIERES LIMITED SP ZOO (trading as Polar Tensor Europe) is registered in
            Poland under RDWW-673 and can operate across the EU/EEA under passporting
            regulations.
          </Typography>
          <Chip
            label={`EU Member States (${EU_COUNTRIES.length})`}
            size='small'
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '0.6875rem',
              backgroundColor: 'rgba(59,130,246,0.1)',
              color: '#2563eb',
              border: '1px solid rgba(59,130,246,0.2)',
            }}
          />
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {EU_COUNTRIES.map((c) => (
              <Grid key={c} size={{ xs: 6, sm: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Dot />
                  <Typography variant='body2' sx={{ color: '#171717' }}>
                    {c}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Chip
            label={`EEA Countries (${EEA_COUNTRIES.length})`}
            size='small'
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '0.6875rem',
              backgroundColor: 'rgba(99,102,241,0.1)',
              color: '#4f46e5',
              border: '1px solid rgba(99,102,241,0.2)',
            }}
          />
          <Grid container spacing={1}>
            {EEA_COUNTRIES.map((c) => (
              <Grid key={c} size={{ xs: 6, sm: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Dot />
                  <Typography variant='body2' sx={{ color: '#171717' }}>
                    {c}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Provinces Dialog */}
      <Dialog
        open={provincesOpen}
        onClose={() => setProvincesOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff !important',
            backgroundImage: 'none !important',
          },
        }}
      >
        <DialogTitle sx={{ color: '#171717' }}>
          Provinces of MSB Activities
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ color: '#6b7280', mb: 3 }}>
            Bitbanki Ltd. is registered for MSB activities across the listed Canadian
            provinces and territories.
          </Typography>
          <Grid container spacing={1}>
            {CANADA_PROVINCES.map((p) => (
              <Grid key={p} size={{ xs: 6, sm: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Dot />
                  <Typography variant='body2' sx={{ color: '#171717' }}>
                    {p}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}
